import { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import Layout from '../../components/Layout'
import Modal from '../../components/shared/Modal'
import { sortByPerson } from '../../utils/peopleSort'
import { Building2, Users, ClipboardCheck, CreditCard, Plus, Trash2, Pencil } from 'lucide-react'
import { PlanCard } from '../../components/billing/BillingUI'
import type { BillingPayer, BillingPlan } from '../../types'

interface Props {
  errors?: Record<string, string | string[]>
  assignable_roles?: string[]
  sendgrid_active?: boolean
  billing_enabled?: boolean
  trial_days?: number | null
  plans?: BillingPlan[]
  billing_plans?: BillingPlan[]
  suggested_plan_key?: string
}

interface Person {
  email: string
  first_name: string
  last_name: string
  role: string
  delivery: 'invite' | 'temp_password'
  temp_password: string
  confirm_existing: boolean
}

const ROLE_LABELS: Record<string, string> = {
  observer: 'Observer',
  member: 'Member',
  admin: 'Admin',
  account_owner: 'Account Owner',
}

const ADMIN_ROLES = ['admin', 'account_owner']

function generatePassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
  let out = ''
  const arr = new Uint32Array(14)
  crypto.getRandomValues(arr)
  arr.forEach(n => { out += chars[n % chars.length] })
  return out
}

function errText(errors: Record<string, string | string[]>, key: string): string | undefined {
  const v = errors[key]
  if (!v) return undefined
  return Array.isArray(v) ? v.join(', ') : v
}

const emptyPerson = (): Person => ({
  email: '', first_name: '', last_name: '', role: 'member',
  delivery: 'invite', temp_password: '', confirm_existing: false,
})

export default function GuideTenantsNew({
  errors = {},
  assignable_roles = ['observer', 'member', 'admin', 'account_owner'],
  billing_enabled = false,
  trial_days,
  plans = [],
  billing_plans = [],
}: Props) {
  const [step, setStep] = useState(1)
  const [tenant, setTenant] = useState({ name: '', slug: '', timezone: 'UTC' })
  const [people, setPeople] = useState<Person[]>([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [sendWelcomeEmails, setSendWelcomeEmails] = useState(false)
  const availablePlans = plans.length ? plans : billing_plans
  const [planKey, setPlanKey] = useState('')
  const [payer, setPayer] = useState<BillingPayer | ''>('')
  const [trialChoice, setTrialChoice] = useState<'trial' | 'immediate' | ''>('')

  const baseErr = errText(errors, 'base') || errText(errors, 'name')

  // A person-level error keyed person_<idx>; the confirm_existing sentinel is
  // prefixed "confirm_existing:" so the UI can render a confirm prompt.
  const personError = (idx: number) => errText(errors, `person_${idx}`)

  const openAdd = () => { setEditIndex(null); setDialogOpen(true) }
  const openEdit = (idx: number) => { setEditIndex(idx); setDialogOpen(true) }

  const savePerson = (p: Person) => {
    if (editIndex === null) setPeople([...people, p])
    else setPeople(people.map((x, i) => (i === editIndex ? p : x)))
    setDialogOpen(false)
  }

  const removePerson = (idx: number) => setPeople(people.filter((_, i) => i !== idx))

  const confirmPerson = (idx: number) =>
    setPeople(people.map((p, i) => (i === idx ? { ...p, confirm_existing: true } : p)))

  const submit = () => {
    setSubmitting(true)
    router.post('/guide_tenants', {
      send_welcome_emails: sendWelcomeEmails,
      tenant: { ...tenant, timezone: tenant.timezone.trim() || 'UTC' },
      people: people.map(p => ({ ...p, confirm_existing: p.confirm_existing ? '1' : '0' })),
      ...(billing_enabled ? { billing: {
        plan_key: planKey,
        payer,
        start_with_trial: trialChoice === 'trial',
      } } : {}),
    }, { onFinish: () => setSubmitting(false) })
  }

  // Task #613: people read alphabetically, but edit/remove/error keys are
  // positional, so carry each person's original index through the sort.
  const orderedPeople = sortByPerson(
    people.map((p, idx) => ({ p, idx })),
    entry => entry.p
  )

  const canNextFromStep1 = tenant.name.trim().length > 0
  const hasAdmin = people.some(p => p.role === 'admin' || p.role === 'account_owner')
  const canNextFromStep2 = people.length > 0 && hasAdmin
  const peopleGuardMessage =
    people.length === 0
      ? 'Add at least one person to continue.'
      : !hasAdmin
        ? 'At least one person must be an Admin or Account Owner.'
        : null
  const canNextFromBilling = Boolean(availablePlans.some(plan => plan.key === planKey && plan.configured !== false) && payer && trialChoice)
  const selectedPlan = availablePlans.find(plan => plan.key === planKey)

  return (
    <Layout>
      <Head title="Create Account - Pinnacle" />
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <Link href="/guide_dashboard" className="text-sm text-pinnacle-red hover:underline">&larr; All Accounts</Link>
          <h1 className="text-3xl font-bold text-text-primary font-heading mt-2">Create a new account</h1>
          <p className="text-base text-text-muted mt-1">
            Set up a new company, add its first people, and get their invitations out. You will be attached as its Guide automatically.
          </p>
        </div>

        <StepIndicator step={step} billingEnabled={billing_enabled} />
        {!billing_enabled && <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">New billing enrollment is disabled. You can still create the company and invite its people, but no billing terms, charge, or trial will be started. Return to <Link href="/guide_billing" className="font-semibold underline">Guide Billing</Link> to enroll when billing becomes available.</div>}

        {baseErr && <div className="px-3 py-2 bg-red-50 text-red-700 rounded text-sm">{baseErr}</div>}

        {step === 1 && (
          <div className="bg-white rounded-xl border border-stone/20 shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-text-primary">Company details</h2>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Company name<span className="text-pinnacle-red"> *</span></label>
              <input
                type="text"
                value={tenant.name}
                onChange={e => setTenant({ ...tenant, name: e.target.value })}
                className="w-full px-3 py-2 border border-stone/30 rounded-lg"
                placeholder="Acme Inc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Slug (optional)</label>
              <input
                type="text"
                value={tenant.slug}
                onChange={e => setTenant({ ...tenant, slug: e.target.value })}
                className="w-full px-3 py-2 border border-stone/30 rounded-lg"
                placeholder="Auto-generated from name if blank"
              />
            </div>
            <div>
              <label htmlFor="tenant-timezone" className="block text-sm font-medium text-text-primary mb-1">Timezone</label>
              <input id="tenant-timezone" type="text" value={tenant.timezone} onChange={e => setTenant({ ...tenant, timezone: e.target.value })} className="w-full px-3 py-2 border border-stone/30 rounded-lg" placeholder="UTC" />
              <p className="mt-1 text-xs text-text-muted">Use an IANA timezone, such as America/New_York. Default: UTC.</p>
              {errText(errors, 'timezone') && <p className="text-sm text-red-700">{errText(errors, 'timezone')}</p>}
            </div>
            <div className="flex justify-end pt-2">
              <button
                onClick={() => setStep(2)}
                disabled={!canNextFromStep1}
                className="px-4 py-2 bg-pinnacle-red text-white rounded-lg text-sm font-semibold hover:bg-pinnacle-red/90 disabled:opacity-50"
              >
                Next: Add people
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white rounded-xl border border-stone/20 shadow-sm p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-text-primary">Add people</h2>
              <button onClick={openAdd} className="flex items-center gap-1 px-3 py-1.5 bg-pinnacle-red text-white rounded-lg text-sm font-semibold hover:bg-pinnacle-red/90">
                <Plus className="w-4 h-4" /> Add person
              </button>
            </div>
            <p className="text-sm text-text-muted">
              Add at least one Admin or Account Owner so someone in the company can manage it. Each person gets an invitation link or a temporary password. Emailing invitations is optional at review.
            </p>

            {people.length === 0 ? (
              <p className="text-sm text-text-muted border border-dashed border-stone/30 rounded-lg p-6 text-center">No people yet. Click "Add person" to get started.</p>
            ) : (
              <ul className="divide-y divide-stone/10">
                {orderedPeople.map(({ p, idx }) => (
                  <li key={idx} className="py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-text-primary">{p.first_name} {p.last_name} <span className="font-normal text-text-muted">({p.email})</span></p>
                      <p className="text-xs text-text-muted mt-0.5">
                        {ROLE_LABELS[p.role] || p.role} · {p.delivery === 'temp_password' ? 'Temporary password' : 'Invitation link'}
                        {p.confirm_existing && ' · Confirmed existing user'}
                      </p>
                      <PersonErrorNotice message={personError(idx)} onConfirm={() => confirmPerson(idx)} confirmed={p.confirm_existing} />
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => openEdit(idx)} className="p-1.5 text-text-secondary hover:text-text-primary rounded hover:bg-stone/10" aria-label="Edit person"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => removePerson(idx)} className="p-1.5 text-text-secondary hover:text-pinnacle-red rounded hover:bg-stone/10" aria-label="Remove person"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex items-center justify-between pt-2 gap-3">
              <button onClick={() => setStep(1)} className="px-4 py-2 border border-stone/30 rounded-lg text-sm">Back</button>
              <div className="flex items-center gap-3">
                {peopleGuardMessage && <p className="text-sm text-text-muted">{peopleGuardMessage}</p>}
                <button
                  onClick={() => setStep(3)}
                  disabled={!canNextFromStep2}
                  className="px-4 py-2 bg-pinnacle-red text-white rounded-lg text-sm font-semibold hover:bg-pinnacle-red/90 disabled:opacity-50"
                >
                  {billing_enabled ? 'Next: Billing' : 'Next: Review'}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && billing_enabled && (
          <div className="bg-white rounded-xl border border-stone/20 shadow-sm p-6 space-y-6">
            {errText(errors, 'billing') && <div className="px-3 py-2 bg-red-50 text-red-700 rounded text-sm">{errText(errors, 'billing')}</div>}
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Subscription tier</h2>
              <p className="text-sm text-text-muted mt-1 mb-4">Choose the plan that matches this company's current size.</p>
              {availablePlans.length === 0 ? (
                <p className="rounded-lg bg-amber-50 text-amber-800 p-3 text-sm">No billing plans are available. Ask a Super Admin to configure billing before creating this account.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {availablePlans.map(plan => (
                    <PlanCard key={plan.key} plan={plan} selected={plan.key === planKey} onClick={plan.configured === false ? undefined : () => setPlanKey(plan.key)} />
                  ))}
                </div>
              )}
            </div>

            <ChoiceSection title="When does billing start?">
              <ChoiceCard
                selected={trialChoice === 'trial'}
                onClick={() => setTrialChoice('trial')}
                title={`Start with a ${trial_days}-day free trial`}
                description={`The ${trial_days}-day trial starts only after card setup. No trial end date or charge is scheduled at company creation.`}
              />
              <ChoiceCard
                selected={trialChoice === 'immediate'}
                onClick={() => setTrialChoice('immediate')}
                title="Bill immediately"
                description="No charge at company creation. Billing starts when the payer completes card setup; renewals follow the confirmed subscription date."
              />
            </ChoiceSection>

            <ChoiceSection title="Who pays for this subscription?">
              <ChoiceCard
                selected={payer === 'guide'}
                onClick={() => setPayer('guide')}
                title="I pay (Guide-paid)"
                description="Use your billing account. The company sees its plan, but not pricing or invoices."
              />
              <ChoiceCard
                selected={payer === 'company'}
                onClick={() => setPayer('company')}
                title="The company pays"
                description="A company Admin adds the company payment method and manages billing in Stripe."
              />
            </ChoiceSection>

            <div className="flex items-center justify-between pt-3 border-t border-stone/10 gap-3">
              <button onClick={() => setStep(2)} className="px-4 py-2 border border-stone/30 rounded-lg text-sm">Back</button>
              <div className="flex items-center gap-3">
                {canNextFromBilling && selectedPlan && (
                  <p className="hidden sm:block text-sm text-text-muted">
                    {payer === 'guide' ? 'Guide-paid' : 'Company-paid'} · {trialChoice === 'trial' ? `${trial_days}-day trial starts after card setup` : 'billing starts after card setup'} · {selectedPlan.price_display || `$${selectedPlan.price_monthly}`}/mo
                  </p>
                )}
                <button
                  onClick={() => setStep(4)}
                  disabled={!canNextFromBilling}
                  className="px-4 py-2 bg-pinnacle-red text-white rounded-lg text-sm font-semibold hover:bg-pinnacle-red/90 disabled:opacity-50"
                >
                  Next: Review
                </button>
              </div>
            </div>
          </div>
        )}

        {step === (billing_enabled ? 4 : 3) && (
          <div className="bg-white rounded-xl border border-stone/20 shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-text-primary">Review</h2>
            <div className="rounded-lg bg-stone/5 border border-stone/20 p-4">
              <p className="text-sm text-text-muted">Company</p>
              <p className="text-base font-semibold text-text-primary">{tenant.name}</p>
              <p className="text-sm text-text-muted">Timezone: {tenant.timezone || 'UTC'}</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-text-primary mb-2">People ({people.length})</p>
              {people.length === 0 ? (
                <p className="text-sm text-text-muted">No people added yet — go back and add at least one Admin or Account Owner.</p>
              ) : (
                <ul className="space-y-1">
                  {orderedPeople.map(({ p, idx }) => (
                    <li key={idx} className="text-sm text-text-primary">
                      {p.first_name} {p.last_name} ({p.email}) — {ROLE_LABELS[p.role] || p.role}, {p.delivery === 'temp_password' ? 'temporary password' : 'invitation link'}
                      <PersonErrorNotice message={personError(idx)} onConfirm={() => confirmPerson(idx)} confirmed={p.confirm_existing} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {billing_enabled && (
              <div className="rounded-lg bg-stone/5 border border-stone/20 p-4">
                <p className="text-sm text-text-muted">Billing</p>
                <p className="text-base font-semibold text-text-primary">{selectedPlan?.name || planKey}</p>
                <p className="text-sm text-text-muted">
                  {payer === 'guide' ? 'Guide-paid' : 'Company-paid'} · {trialChoice === 'trial' ? `${trial_days}-day trial starts after card setup` : 'Bill immediately after card setup'}
                  {selectedPlan ? ` · ${selectedPlan.price_display || `$${selectedPlan.price_monthly}`}/mo` : ''}
                </p>
                <p className="text-sm text-text-muted mt-1">
                  Creating this company does not charge a card or start a trial. The selected payer must complete payment setup before subscription terms begin.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                <input type="checkbox" checked={sendWelcomeEmails} onChange={e => setSendWelcomeEmails(e.target.checked)} aria-describedby="setup-email-scope" />
                Send welcome/invitation emails
              </label>
              <p id="setup-email-scope" className="text-sm text-text-muted">
                Only people using invitation links will be emailed, subject to system email policy. Temporary-password users and your Guide account will not be emailed.
              </p>
              <p className="text-sm text-text-muted">Leave unchecked to create without setup emails. You can still copy invitations and credentials or explicitly send invitations afterward.</p>
            </div>
            <div className="flex justify-between pt-2">
              <button onClick={() => setStep(billing_enabled ? 3 : 2)} className="px-4 py-2 border border-stone/30 rounded-lg text-sm">Back</button>
              <button
                onClick={submit}
                disabled={submitting || !canNextFromStep1 || !canNextFromStep2 || (billing_enabled && !canNextFromBilling)}
                className="px-4 py-2 bg-pinnacle-red text-white rounded-lg text-sm font-semibold hover:bg-pinnacle-red/90 disabled:opacity-50"
              >
                {submitting ? 'Creating…' : 'Create account'}
              </button>
            </div>
          </div>
        )}
      </div>

      <PersonDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={savePerson}
        initial={editIndex !== null ? people[editIndex] : null}
        roles={assignable_roles}
      />
    </Layout>
  )
}

function StepIndicator({ step, billingEnabled }: { step: number; billingEnabled: boolean }) {
  const steps = [
    { n: 1, label: 'Company', icon: <Building2 className="w-4 h-4" /> },
    { n: 2, label: 'People', icon: <Users className="w-4 h-4" /> },
    ...(billingEnabled ? [{ n: 3, label: 'Billing', icon: <CreditCard className="w-4 h-4" /> }] : []),
    { n: billingEnabled ? 4 : 3, label: 'Review', icon: <ClipboardCheck className="w-4 h-4" /> },
  ]
  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => (
        <div key={s.n} className="flex items-center gap-2">
          {i > 0 && <div className="w-8 h-px bg-stone/30" />}
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${
            step === s.n ? 'bg-pinnacle-red text-white' : step > s.n ? 'bg-stone/20 text-text-primary' : 'bg-stone/10 text-text-muted'
          }`}>
            {s.icon} {s.label}
          </div>
        </div>
      ))}
    </div>
  )
}

function ChoiceSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-stone/10 pt-5">
      <h2 className="text-lg font-semibold text-text-primary mb-3">{title}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  )
}
function PersonErrorNotice({ message, onConfirm, confirmed }: { message?: string; onConfirm: () => void; confirmed: boolean }) {
  if (!message) return null
  const isConfirm = message.startsWith('confirm_existing:')
  if (isConfirm && confirmed) return null
  return (
    <div className="mt-1 px-2 py-1.5 bg-red-50 text-red-700 rounded text-xs">
      {isConfirm ? (
        <>
          {message.replace('confirm_existing:', '')}
          <button onClick={onConfirm} className="ml-2 font-semibold underline">Yes, add them</button>
        </>
      ) : message}
    </div>
  )
}

function PersonDialog({ isOpen, onClose, onSave, initial, roles }: {
  isOpen: boolean
  onClose: () => void
  onSave: (p: Person) => void
  initial: Person | null
  roles: string[]
}) {
  const [form, setForm] = useState<Person>(emptyPerson())
  const [wasOpen, setWasOpen] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  // Reset form contents each time the dialog opens.
  if (isOpen && !wasOpen) {
    setForm(initial ? { ...initial } : emptyPerson())
    setLocalError(null)
    setWasOpen(true)
  } else if (!isOpen && wasOpen) {
    setWasOpen(false)
  }

  const save = () => {
    if (!form.email.trim() || !form.first_name.trim() || !form.last_name.trim()) {
      setLocalError('First name, last name, and email are required.')
      return
    }
    if (form.delivery === 'temp_password' && form.temp_password.length < 6) {
      setLocalError('Temporary password must be at least 6 characters.')
      return
    }
    onSave({ ...form, email: form.email.trim(), confirm_existing: false })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={initial ? 'Edit person' : 'Add person'}>
      <div className="space-y-4">
        {localError && <div className="px-3 py-2 bg-red-50 text-red-700 rounded text-sm">{localError}</div>}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">First name<span className="text-pinnacle-red"> *</span></label>
            <input type="text" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Last name<span className="text-pinnacle-red"> *</span></label>
            <input type="text" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Email<span className="text-pinnacle-red"> *</span></label>
          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm" placeholder="person@company.com" />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Role</label>
          <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm">
            <optgroup label="Administrators">
              {roles.filter(r => ADMIN_ROLES.includes(r)).map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
            </optgroup>
            <optgroup label="Regular users">
              {roles.filter(r => !ADMIN_ROLES.includes(r)).map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
            </optgroup>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">How do they get access?</label>
          <div className="space-y-2">
            <label className="flex items-start gap-2 text-sm text-text-primary">
              <input type="radio" className="mt-0.5" checked={form.delivery === 'invite'} onChange={() => setForm({ ...form, delivery: 'invite' })} />
              <span><span className="font-medium">Invitation link</span> — they set their own password when accepting. You can copy the email or send it directly.</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-text-primary">
              <input type="radio" className="mt-0.5" checked={form.delivery === 'temp_password'} onChange={() => setForm({ ...form, delivery: 'temp_password' })} />
              <span><span className="font-medium">Temporary password</span> — they log in with it and must change it on first login.</span>
            </label>
          </div>
        </div>

        {form.delivery === 'temp_password' && (
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Temporary password<span className="text-pinnacle-red"> *</span></label>
            <input type="text" value={form.temp_password} onChange={e => setForm({ ...form, temp_password: e.target.value })} className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm font-mono" placeholder="min 6 characters" />
            <button type="button" onClick={() => setForm({ ...form, temp_password: generatePassword() })} className="text-xs text-pinnacle-red hover:underline mt-1">Generate password</button>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-stone/30 rounded-lg text-sm">Cancel</button>
          <button onClick={save} className="px-4 py-2 bg-pinnacle-red text-white rounded-lg text-sm font-semibold hover:bg-pinnacle-red/90">
            {initial ? 'Save changes' : 'Add person'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function ChoiceCard({ selected, onClick, title, description }: {
  selected: boolean
  onClick: () => void
  title: string
  description: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-start gap-3 rounded-xl p-4 text-left border ${selected ? 'border-2 border-pinnacle-red bg-red-50/30' : 'border-stone/20 bg-white'}`}
    >
      <span className={`mt-0.5 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-pinnacle-red' : 'border-stone/40'}`}>
        {selected && <span className="w-2.5 h-2.5 rounded-full bg-pinnacle-red" />}
      </span>
      <span>
        <span className="block text-sm font-bold text-text-primary">{title}</span>
        <span className="block text-sm text-text-muted mt-1">{description}</span>
      </span>
    </button>
  )
}
